import { z } from 'zod';
/**
 * Event, venue, gate, zone, ticket-product and capacity contracts
 * (SDAD Sections 14, 15).
 *
 * The organizer-facing and attendee-facing shapes are declared separately and
 * deliberately. The public event page is **not** a serialization of organizer
 * configuration: gate policy, zone mapping, internal notes and capacity are
 * Class C organizer-private data (SDAD Section 40), and a projection that
 * happened to include them would leak operational security through a page
 * anybody can load.
 */
// --- Shared primitives ------------------------------------------------------
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(96)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use lowercase letters, numbers and hyphens.');
/**
 * A stable machine identifier an organizer chooses once.
 *
 * Codes are referenced by gate packages, scanner provisioning and later order
 * lines. They are immutable; display names are not.
 */
export const stableCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use capitals, numbers and underscores, starting with a letter.');
/**
 * An IANA timezone name.
 *
 * Two checks, and the second is the one that matters. `Intl` happily accepts a
 * fixed offset like `+01:00` — which is a perfectly valid thing to say and a
 * disastrous thing to store, because it does not move across a daylight-saving
 * change. An event whose timezone is an offset would silently shift its doors by
 * an hour twice a year. The region/location shape is what excludes offsets and
 * bare abbreviations, and the runtime lookup is what excludes typos.
 *
 * PostgreSQL enforces the same thing independently against `pg_timezone_names`,
 * so a path that skipped this validation still cannot store nonsense.
 */
export const timezoneSchema = z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(/^[A-Za-z]+(?:\/[A-Za-z0-9_+-]+)+$/, 'Use a region name such as Africa/Lagos.')
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Not a recognised timezone.' });
/**
 * Money as integer minor units plus an explicit ISO 4217 currency.
 *
 * Never a decimal string and never a float. Nigeria is the first market, so
 * currency is required rather than defaulted — a hard-coded NGN is far harder to
 * remove later than a required field is to fill in now (SDAD Section 4.10).
 */
export const moneySchema = z.object({
    minor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    currency: z
        .string()
        .trim()
        .toUpperCase()
        .length(3)
        .regex(/^[A-Z]{3}$/),
});
export const isoInstantSchema = z.string().datetime({ offset: true }).or(z.string().datetime());
// --- Venues, zones, gates (organizer-private) -------------------------------
export const createVenueRequestSchema = z.object({
    name: z.string().trim().min(2).max(160),
    publicLocality: z.string().trim().min(2).max(120),
    publicAddressLine: z.string().trim().max(300).nullable().default(null),
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .length(2)
        .regex(/^[A-Z]{2}$/)
        .default('NG'),
    timezone: timezoneSchema,
    internalNotes: z.string().trim().max(2000).nullable().default(null),
});
export const venueSchema = z.object({
    venueId: z.string().uuid(),
    name: z.string(),
    publicLocality: z.string(),
    publicAddressLine: z.string().nullable(),
    countryCode: z.string(),
    timezone: z.string(),
    internalNotes: z.string().nullable(),
    status: z.enum(['ACTIVE', 'RETIRED']),
    spaces: z.array(z.object({
        venueSpaceId: z.string().uuid(),
        name: z.string(),
        physicalCapacity: z.number().int().nullable(),
    })),
    zones: z.array(z.object({
        accessZoneId: z.string().uuid(),
        code: z.string(),
        displayName: z.string(),
        kind: z.enum(['GENERAL', 'PREMIUM', 'RESTRICTED', 'OPERATIONAL']),
    })),
    gates: z.array(z.object({
        gateId: z.string().uuid(),
        code: z.string(),
        displayName: z.string(),
        isAccessibleEntry: z.boolean(),
        status: z.enum(['ACTIVE', 'RETIRED']),
    })),
});
export const createVenueSpaceRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    physicalCapacity: z.number().int().min(0).nullable().default(null),
});
export const createAccessZoneRequestSchema = z.object({
    code: stableCodeSchema,
    displayName: z.string().trim().min(1).max(80),
    kind: z.enum(['GENERAL', 'PREMIUM', 'RESTRICTED', 'OPERATIONAL']).default('GENERAL'),
});
/** Renaming is allowed; re-coding is not. The code is what gates reference. */
export const renameAccessZoneRequestSchema = z.object({
    displayName: z.string().trim().min(1).max(80),
});
export const createGateRequestSchema = z.object({
    code: stableCodeSchema,
    displayName: z.string().trim().min(1).max(80),
    isAccessibleEntry: z.boolean().default(false),
    internalNotes: z.string().trim().max(2000).nullable().default(null),
});
// --- Events (organizer-private until published) -----------------------------
export const eventStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'UNPUBLISHED', 'CANCELLED']);
export const createEventRequestSchema = z.object({
    title: z.string().trim().min(2).max(160),
    slug: slugSchema,
    timezone: timezoneSchema,
    venueId: z.string().uuid().nullable().default(null),
    publicSummary: z.string().trim().max(300).nullable().default(null),
});
export const updateEventRequestSchema = z.object({
    title: z.string().trim().min(2).max(160),
    publicSummary: z.string().trim().max(300).nullable(),
    publicDescription: z.string().trim().max(8000).nullable(),
    internalNotes: z.string().trim().max(2000).nullable(),
    venueId: z.string().uuid().nullable(),
    timezone: timezoneSchema,
});
export const createEventSessionRequestSchema = z.object({
    name: z.string().trim().min(1).max(120).nullable().default(null),
    startsAt: isoInstantSchema,
    endsAt: isoInstantSchema,
    doorsOpenAt: isoInstantSchema.nullable().default(null),
});
export const eventSessionSchema = z.object({
    eventSessionId: z.string().uuid(),
    name: z.string().nullable(),
    startsAt: z.string(),
    endsAt: z.string(),
    doorsOpenAt: z.string().nullable(),
    status: z.enum(['SCHEDULED', 'CANCELLED']),
});
export const eventGatePolicySchema = z.object({
    eventGatePolicyId: z.string().uuid(),
    gateId: z.string().uuid(),
    gateCode: z.string(),
    gateDisplayName: z.string(),
    isAccessibleEntry: z.boolean(),
    revision: z.number().int(),
    admissionOpensAt: z.string().nullable(),
    admissionClosesAt: z.string().nullable(),
    reentryPolicy: z.enum(['NONE', 'SINGLE', 'UNLIMITED']),
    operationalReentryPolicy: z.enum(['NONE', 'SINGLE', 'UNLIMITED']),
    identityCheckRequired: z.boolean(),
    zones: z.array(z.object({
        accessZoneId: z.string().uuid(),
        code: z.string(),
        displayName: z.string(),
    })),
});
export const setEventGatePolicyRequestSchema = z.object({
    gateId: z.string().uuid(),
    admissionOpensAt: isoInstantSchema.nullable().default(null),
    admissionClosesAt: isoInstantSchema.nullable().default(null),
    /**
     * Attendee/ticket re-entry.
     *
     * The enum keeps all three values because the schema and the surface have
     * always offered them, but only NONE is publishable: gate readiness refuses
     * an event whose ticket policy allows re-entry, because that is unimplemented.
     */
    reentryPolicy: z.enum(['NONE', 'SINGLE', 'UNLIMITED']).default('NONE'),
    /**
     * Operational credential re-entry, chosen independently of tickets.
     *
     * Its own field because the two were one value once, and a venue could not be
     * given crew re-entry without claiming attendee re-entry the product cannot
     * enforce — so in practice neither could be published and every badge got a
     * single crossing. Defaults to NONE, which is what existing events already
     * enforce.
     */
    operationalReentryPolicy: z.enum(['NONE', 'SINGLE', 'UNLIMITED']).default('NONE'),
    identityCheckRequired: z.boolean().default(false),
    accessZoneIds: z.array(z.string().uuid()).min(1),
});
// --- Ticket products --------------------------------------------------------
export const ticketProductVersionSchema = z.object({
    ticketProductVersionId: z.string().uuid(),
    version: z.number().int(),
    displayName: z.string(),
    description: z.string().nullable(),
    price: moneySchema,
    accessScope: z.enum(['ALL_SESSIONS', 'SPECIFIC_SESSIONS']),
    sessionIds: z.array(z.string().uuid()),
    accessZoneId: z.string().uuid().nullable(),
    accessZoneDisplayName: z.string().nullable(),
    securityMode: z.enum(['STANDARD_PASS', 'SECURE_PASS']),
    createdAt: z.string(),
    supersededAt: z.string().nullable(),
});
export const ticketProductSchema = z.object({
    ticketProductId: z.string().uuid(),
    code: z.string(),
    status: z.enum(['DRAFT', 'PUBLISHED', 'RETIRED']),
    currentVersion: ticketProductVersionSchema,
    /** Organizer-only. Capacity is not part of the public projection. */
    capacity: z.object({
        inventoryPoolId: z.string().uuid().nullable(),
        inventoryPoolName: z.string().nullable(),
        allocated: z.number().int(),
        committedSales: z.number().int(),
        activeHolds: z.number().int(),
        available: z.number().int(),
    }),
});
export const createTicketProductRequestSchema = z.object({
    code: stableCodeSchema,
    displayName: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2000).nullable().default(null),
    price: moneySchema,
    accessScope: z.enum(['ALL_SESSIONS', 'SPECIFIC_SESSIONS']).default('ALL_SESSIONS'),
    sessionIds: z.array(z.string().uuid()).default([]),
    accessZoneId: z.string().uuid().nullable().default(null),
    securityMode: z.enum(['STANDARD_PASS', 'SECURE_PASS']).default('STANDARD_PASS'),
});
/**
 * Revising terms creates a new version; it never edits one. The request shape is
 * identical to creation apart from the immutable code, which is the point.
 */
export const reviseTicketProductRequestSchema = createTicketProductRequestSchema.omit({
    code: true,
});
// --- Capacity ---------------------------------------------------------------
export const createInventoryPoolRequestSchema = z.object({
    code: stableCodeSchema,
    displayName: z.string().trim().min(1).max(120),
    capacity: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    venueSpaceId: z.string().uuid().nullable().default(null),
});
export const setPoolCapacityRequestSchema = z.object({
    capacity: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
});
export const setAllocationRequestSchema = z.object({
    inventoryPoolId: z.string().uuid(),
    kind: z.enum(['SALEABLE', 'PROTECTED', 'BLOCKED']),
    ticketProductId: z.string().uuid().nullable().default(null),
    quantity: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    reason: z.string().trim().max(300).nullable().default(null),
});
export const inventoryPoolSchema = z.object({
    inventoryPoolId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    capacity: z.number().int(),
    venueSpaceId: z.string().uuid().nullable(),
    saleableAllocated: z.number().int(),
    protectedAllocations: z.number().int(),
    blockedCapacity: z.number().int(),
    committedSales: z.number().int(),
    activeHolds: z.number().int(),
    unallocatedAvailable: z.number().int(),
    allocations: z.array(z.object({
        allocationId: z.string().uuid(),
        kind: z.enum(['SALEABLE', 'PROTECTED', 'BLOCKED']),
        ticketProductId: z.string().uuid().nullable(),
        ticketProductCode: z.string().nullable(),
        quantity: z.number().int(),
        reason: z.string().nullable(),
    })),
});
// --- The organizer's whole event --------------------------------------------
export const organizerEventSchema = z.object({
    eventId: z.string().uuid(),
    organizationId: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    publicSummary: z.string().nullable(),
    publicDescription: z.string().nullable(),
    internalNotes: z.string().nullable(),
    timezone: z.string(),
    status: eventStatusSchema,
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    venue: z
        .object({
        venueId: z.string().uuid(),
        name: z.string(),
        publicLocality: z.string(),
        timezone: z.string(),
    })
        .nullable(),
    sessions: z.array(eventSessionSchema),
    gatePolicies: z.array(eventGatePolicySchema),
    ticketProducts: z.array(ticketProductSchema),
    inventoryPools: z.array(inventoryPoolSchema),
    /** What still stands between this event and publication. */
    publishReadiness: z.object({
        ready: z.boolean(),
        blockers: z.array(z.object({
            code: z.string(),
            message: z.string(),
            /** Where in the workspace the organizer fixes it. */
            section: z.enum(['OVERVIEW', 'SESSIONS', 'VENUE', 'GATES', 'TICKETS']),
        })),
    }),
});
export const publishEventRequestSchema = z.object({
    reasonCode: z.string().trim().min(2).max(64).default('ORGANIZER_PUBLISHED'),
    note: z.string().trim().max(1000).optional(),
});
export const unpublishEventRequestSchema = z.object({
    reasonCode: z.string().trim().min(2).max(64),
    note: z.string().trim().max(1000).optional(),
});
//# sourceMappingURL=event-schemas.js.map